import ExcelJS from "exceljs";
import { saveAs } from "file-saver";

import { groupBy, sortAscByDate } from "components/utils/functions";
import { serviceOrderStatus } from "config/constants";

export const exportTableToExcel = async (tableItems, users = []) => {
  if (!Array.isArray(tableItems) || tableItems.length === 0) return;

  const workbook = new ExcelJS.Workbook();
  const sheet = workbook.addWorksheet("Reporte de Nómina");

  const cols = [
    { header: "Centro de Costo", key: "costCenter", width: 18 },
    { header: "Nombre Centro de Costo", key: "costCenterName", width: 32 },
    ...users.map((u) => ({ header: u.name, key: `var_${u.id}`, width: 18 })),
  ];
  sheet.columns = cols;

  for (const row of tableItems) {
    const rowObj = {
      costCenter: row.costCenter ?? "",
      costCenterName: row.costCenterName ?? "",
      ...users.reduce((acc, u) => {
        acc[`var_${u.id}`] = row[`var_${u.id}`] ?? "";
        return acc;
      }, {}),
    };
    sheet.addRow(rowObj);
  }

  const headerFillBlue = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: "BDD7EE" },
  };
  const headerFillWhite = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: "FFFFFFFF" },
  };

  const headerRow = sheet.getRow(1);
  headerRow.height = 20;
  headerRow.eachCell((cell, colNumber) => {
    cell.font = { bold: true, color: { argb: "000000" } };
    cell.fill = colNumber <= 2 ? headerFillWhite : headerFillBlue;
    cell.alignment = { horizontal: "center", vertical: "middle" };
    cell.border = {
      top: { style: "thin" },
      left: { style: "thin" },
      bottom: { style: "thin" },
      right: { style: "thin" },
    };
  });

  sheet.eachRow((r, rowNumber) => {
    if (rowNumber === 1) return;
    r.eachCell((cell) => {
      cell.alignment = { horizontal: "center", vertical: "middle" };
      cell.border = {
        top: { style: "thin" },
        left: { style: "thin" },
        bottom: { style: "thin" },
        right: { style: "thin" },
      };
    });
  });

  const buf = await workbook.xlsx.writeBuffer();
  saveAs(new Blob([buf]), "reporte-nomina.xlsx");
};

export const computeByUserPercentages = (data, opts = {}) => {
  const { asPercentString = true, decimals = 2 } = opts;
  const map = {};

  // Group by userExecutor and suming durations per costCenterID
  for (const { userExecutor, costCenterID, totalDuration } of data) {
    if (!map[userExecutor]) map[userExecutor] = { total: 0, centers: {} };
    map[userExecutor].total += totalDuration;
    map[userExecutor].centers[costCenterID] =
      (map[userExecutor].centers[costCenterID] || 0) + totalDuration;
  }

  // Format result calculating percentages regarding the user's total
  const result = Object.entries(map).map(
    ([userExecutor, { total, centers }]) => {
      const details = Object.entries(centers).map(
        ([costCenterID, totalDuration]) => {
          const raw = total === 0 ? 0 : totalDuration / total;
          const percentage = asPercentString
            ? (raw * 100).toFixed(decimals) + "%"
            : Number(raw.toFixed(decimals));
          return { costCenterID, totalDuration, percentage };
        }
      );

      return { userExecutor, totalUserDuration: total, details };
    }
  );

  return result;
};

const calculateDuration = (events, options = {}) => {
  const { countRunning = false } = options;
  const sorted = sortAscByDate(events, "createdAt");

  let durationMs = 0;
  let lastExecutingTs = null;

  for (const ev of sorted) {
    const ts = new Date(ev.createdAt).getTime();
    if (
      ev.status === serviceOrderStatus.executing &&
      lastExecutingTs === null
    ) {
      if (lastExecutingTs === null) lastExecutingTs = ts;
    } else if (
      ev.status === serviceOrderStatus.paused ||
      ev.status === serviceOrderStatus.executed
    ) {
      if (lastExecutingTs !== null) {
        durationMs += ts - lastExecutingTs;
        lastExecutingTs = null;
      }
    }
  }

  if (lastExecutingTs !== null && countRunning) {
    durationMs += Date.now() - lastExecutingTs;
  }

  const durationSec = durationMs / 1000;
  return Math.round(durationSec);
};

export const computeGroupedDurations = (orders, options = {}) => {
  const grouped = groupBy(orders, (o) => o.serviceOrderID);
  return Object.keys(grouped).map((serviceOrderID) => {
    const events = sortAscByDate(grouped[serviceOrderID], "createdAt");
    const duration = calculateDuration(events, options);
    return { serviceOrderID, duration, events };
  });
};
